import { z } from 'zod';
import { InvalidStatusTransitionError } from './errors.js';
import type { Permission } from './permissions.js';

/**
 * The operational axis since docs/decisions/0077-a-donation-waits-in-pending-and-nobody-approves-it.md:
 * a donation is saved straight into `pending`, taken into its period's order as
 * `queued`, carried out as `fulfilled`, or `cancelled`. Nothing approves it.
 */
export const DONATION_STATUSES = ['pending', 'queued', 'fulfilled', 'cancelled'] as const;
export type DonationStatus = (typeof DONATION_STATUSES)[number];
export const donationStatusSchema = z.enum(DONATION_STATUSES);

/**
 * The four statuses 0077 retired. No row holds one — migration `0110` moved
 * every such row to `pending` — but the trail does: a `transition` or `submit`
 * audit row written before it names them, and the timeline still reads them.
 */
export const RETIRED_DONATION_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'rejected',
] as const;
export type RetiredDonationStatus = (typeof RETIRED_DONATION_STATUSES)[number];

/** A status as the trail may name it: today's, or one a row held before 0077. */
export type RecordedDonationStatus = DonationStatus | RetiredDonationStatus;

export const PAYMENT_STATUSES = [
  'unpaid',
  'partially_settled',
  'settled',
  'refunded',
  'reversed',
  'not_charged',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);

/**
 * Whether no money has reached the donation — the question every refusal on
 * the operational axis actually asks. `unpaid` owes money that has not come;
 * `not_charged` owes none and never will, which is a different answer to "what
 * is outstanding" and the same answer to "may this be cancelled or corrected
 * without a reversal" — docs/decisions/0073-a-campaign-may-take-no-money-and-its-yurt-is-told.md.
 */
export function hasReceivedNoMoney(paymentStatus: PaymentStatus): boolean {
  return paymentStatus === 'unpaid' || paymentStatus === 'not_charged';
}

/**
 * The operational axis, as docs/decisions/0077 leaves 0023's table. Every edge
 * here is a row of it; nothing else is an edge. `pending ↔ queued` moves both
 * ways — `queued → pending` is a dequeue — and `pending → fulfilled` is what
 * `approved → fulfilled` was, for work carried out without a queue.
 *
 * `fulfilled → queued` and `fulfilled → pending` are the undo of a mistaken
 * fulfilment — back to the order when the donation holds a place in it,
 * back to waiting when it never did — and nothing else. It is not a lifecycle
 * edge a screen is offered from `donation.transitions`: it has its own route,
 * permission and refusals (docs/decisions/0091).
 */
const DONATION_TRANSITIONS: Record<DonationStatus, readonly DonationStatus[]> = {
  pending: ['queued', 'fulfilled', 'cancelled'],
  queued: ['pending', 'fulfilled', 'cancelled'],
  fulfilled: ['queued', 'pending', 'cancelled'],
  cancelled: [],
};

/**
 * The financial axis, moved only by the reconciliation module. It never
 * consults the operational axis: money that has demonstrably arrived is
 * recorded whatever the record's operational state.
 *
 * `not_charged` has no edge in or out: a donation of a `no_charge` campaign is
 * born there and stays there, so nothing — a match, a refund, a reversal — can
 * move it, and nothing can move a charged donation into it. The database says
 * the same with `donations_amount_matches_charge` and the trigger
 * `donations_payment_status_follows_pricing_mode`.
 */
const PAYMENT_TRANSITIONS: Record<PaymentStatus, readonly PaymentStatus[]> = {
  unpaid: ['partially_settled', 'settled'],
  partially_settled: ['unpaid', 'settled', 'refunded', 'reversed'],
  settled: ['partially_settled', 'refunded', 'reversed'],
  refunded: [],
  reversed: ['unpaid'],
  not_charged: [],
};

export function canTransitionDonation(from: DonationStatus, to: DonationStatus): boolean {
  return DONATION_TRANSITIONS[from].includes(to);
}

export function assertDonationTransition(from: DonationStatus, to: DonationStatus): void {
  if (!canTransitionDonation(from, to)) {
    throw new InvalidStatusTransitionError('donation.status', from, to);
  }
}

export function canTransitionPayment(from: PaymentStatus, to: PaymentStatus): boolean {
  return PAYMENT_TRANSITIONS[from].includes(to);
}

export function assertPaymentTransition(from: PaymentStatus, to: PaymentStatus): void {
  if (!canTransitionPayment(from, to)) {
    throw new InvalidStatusTransitionError('donation.paymentStatus', from, to);
  }
}

export function nextDonationStatuses(from: DonationStatus): readonly DonationStatus[] {
  return DONATION_TRANSITIONS[from];
}

export function nextPaymentStatuses(from: PaymentStatus): readonly PaymentStatus[] {
  return PAYMENT_TRANSITIONS[from];
}

export function isTerminalDonationStatus(status: DonationStatus): boolean {
  return DONATION_TRANSITIONS[status].length === 0;
}

export function isTerminalPaymentStatus(status: PaymentStatus): boolean {
  return PAYMENT_TRANSITIONS[status].length === 0;
}

/**
 * Whether money may still be matched to a donation in this status. A
 * `cancelled` record is one the organization has withdrawn, so money arriving
 * against it leaves an unmatched bank line for an operator to work; every
 * other status is a donation on the books.
 */
export function isMatchableDonationStatus(status: DonationStatus): boolean {
  return status !== 'cancelled';
}

/** The same predicate as a list, derived so the two cannot disagree. */
export const MATCHABLE_DONATION_STATUSES: readonly DonationStatus[] =
  DONATION_STATUSES.filter(isMatchableDonationStatus);

/**
 * Whether a donation counts toward `pledged_minor` and `donation_count` —
 * docs/decisions/0014-campaign-goals-and-totals.md. Since 0077 a donation is
 * on the books from the moment it is saved, so only a cancellation takes it
 * off them.
 */
export function countsTowardPledged(status: DonationStatus): boolean {
  return status !== 'cancelled';
}

/**
 * The same predicate as a list, for a query that must filter rather than ask
 * row by row. Derived from `countsTowardPledged` so the two cannot disagree
 * about what a pledge is.
 */
export const PLEDGED_DONATION_STATUSES: readonly DonationStatus[] =
  DONATION_STATUSES.filter(countsTowardPledged);

/**
 * The states in which a donation may still be corrected — its donor, answers
 * and parties as well as its amount, quantity, period and date, as a draft
 * once was. A `fulfilled` row has work and a cost booked against it, and a
 * `cancelled` one has already given its pledge back, so moving its amount
 * would move a figure that is no longer counted — docs/decisions/0077.
 *
 * It is asked twice: by the service before it re-prices, and again by the
 * writer under the row's `FOR UPDATE` lock, because a cancellation committing
 * in between would otherwise leave the period holding a pledge for a donation
 * that no longer has one.
 */
export const CORRECTABLE_DONATION_STATUSES: readonly DonationStatus[] = ['pending', 'queued'];

/**
 * Why a correction of a donation in the fulfilment order is barred for this
 * editor, or null when it is not. Only a `queued` donation is locked: it is
 * what the field team carries out, so it leaves the entering clerk's hands and
 * takes `donation.editQueued`. Since 0077 the lock is the permission alone —
 * the rule that barred the holder who entered it was four eyes, and four eyes
 * is gone — docs/decisions/0071-a-ready-donation-waits-and-the-order-locks-it.md.
 */
export type QueuedEditBar = 'needs_permission';

export function queuedEditBar(
  row: { readonly status: DonationStatus },
  editor: { readonly permissions: readonly Permission[] },
): QueuedEditBar | null {
  if (row.status !== 'queued') return null;
  if (!editor.permissions.includes('donation.editQueued')) return 'needs_permission';
  return null;
}

/**
 * The statuses in which a donation's entering operator may be changed: the
 * ones an edit reaches, and never a `fulfilled` or `cancelled` one, whose
 * record is closed.
 */
export const OPERATOR_CHANGE_STATUSES: readonly DonationStatus[] = CORRECTABLE_DONATION_STATUSES;

/**
 * Why this editor may not change who a donation is recorded against, or null
 * when they may — §3.2's "authorised users may change it", after entry.
 *
 * - `needs_record_for_operator` / `needs_edit`: the act is both naming an
 *   operator and editing the donation, so it needs both permissions.
 * - `not_staff_entered`: nobody typed it — a portal capture has no operator to
 *   correct, and inventing one is not a correction.
 * - `not_editable`: outside `OPERATOR_CHANGE_STATUSES`.
 * - `needs_edit_queued` on a queued donation: 0071's lock.
 *
 * The payment axis bars nothing: the unpaid gate keeps an edit from moving a
 * figure a match holds, and the operator is not a figure. The operator on
 * record may hand their own donation on: the rule that refused it existed so
 * nobody could approve their own entry, and nothing is approved since 0077.
 */
export type OperatorChangeBar =
  | 'needs_record_for_operator'
  | 'needs_edit'
  | 'not_staff_entered'
  | 'not_editable'
  | 'needs_edit_queued';

export function operatorChangeBar(
  row: { readonly status: DonationStatus; readonly createdByUserId: string | null },
  editor: { readonly permissions: readonly Permission[] },
): OperatorChangeBar | null {
  if (!editor.permissions.includes('donation.recordForOperator')) {
    return 'needs_record_for_operator';
  }
  if (!editor.permissions.includes('donation.edit')) return 'needs_edit';
  if (row.createdByUserId === null) return 'not_staff_entered';
  if (!OPERATOR_CHANGE_STATUSES.includes(row.status)) return 'not_editable';
  if (queuedEditBar(row, editor) === 'needs_permission') return 'needs_edit_queued';
  return null;
}

/**
 * Whether a donation's `fulfillment_cost_minor` belongs in the rollup. Only a
 * `fulfilled` donation has spent anything, and cancelling one takes the spend
 * back out — the same one-path rule as `pledged`.
 */
export function countsTowardFulfillmentCost(status: DonationStatus): boolean {
  return status === 'fulfilled';
}

export type DonationMeasureDirection = -1 | 0 | 1;

/**
 * Which way a status move pushes a measure: `1` when the donation starts
 * counting, `-1` when it stops, `0` when the move does not change whether it
 * counts. `from` is null for a row that did not exist before.
 *
 * Every totals delta in the system is this number times an amount already
 * frozen on the row, which is why cancelling and reversing need no arithmetic
 * of their own — docs/decisions/0014-campaign-goals-and-totals.md.
 */
function direction(
  counts: (status: DonationStatus) => boolean,
  from: DonationStatus | null,
  to: DonationStatus,
): DonationMeasureDirection {
  const before = from === null ? false : counts(from);
  const after = counts(to);
  if (before === after) return 0;
  return after ? 1 : -1;
}

export function pledgedDirection(
  from: DonationStatus | null,
  to: DonationStatus,
): DonationMeasureDirection {
  return direction(countsTowardPledged, from, to);
}

export function fulfillmentCostDirection(
  from: DonationStatus | null,
  to: DonationStatus,
): DonationMeasureDirection {
  return direction(countsTowardFulfillmentCost, from, to);
}

export const DONATION_COMBINATION_KINDS = ['routine', 'real', 'transient'] as const;
export type DonationCombinationKind = (typeof DONATION_COMBINATION_KINDS)[number];

/**
 * Every cell of 0023's combination matrix, as data rather than prose, over the
 * statuses 0077 left.
 *
 * - `routine` — an ordinary state the screens render without comment.
 * - `real` — uncommon, meaningful, and never prevented. `fulfilled` +
 *   `reversed` is a chargeback against aid already distributed; it is a line on
 *   a report, not an error.
 * - `transient` — exists only inside the cancelling transaction, which then
 *   moves the payment axis to `reversed` or `refunded`.
 *
 * `not_charged` is `routine` beside every status: a request that takes no
 * money goes through the queue and fulfilment like any other donation, and
 * its financial axis never moves.
 */
const LIVE_COMBINATIONS: Record<PaymentStatus, DonationCombinationKind> = {
  unpaid: 'routine',
  partially_settled: 'routine',
  settled: 'routine',
  refunded: 'real',
  reversed: 'real',
  not_charged: 'routine',
};

const DONATION_COMBINATIONS: Record<
  DonationStatus,
  Record<PaymentStatus, DonationCombinationKind>
> = {
  pending: LIVE_COMBINATIONS,
  queued: LIVE_COMBINATIONS,
  fulfilled: LIVE_COMBINATIONS,
  cancelled: {
    unpaid: 'routine',
    partially_settled: 'transient',
    settled: 'transient',
    refunded: 'routine',
    reversed: 'routine',
    not_charged: 'routine',
  },
};

export function donationCombination(
  status: DonationStatus,
  paymentStatus: PaymentStatus,
): DonationCombinationKind {
  return DONATION_COMBINATIONS[status][paymentStatus];
}

/** A combination a screen must render rather than treat as a defect. */
export function isReportableCombination(
  status: DonationStatus,
  paymentStatus: PaymentStatus,
): boolean {
  const kind = donationCombination(status, paymentStatus);
  return kind === 'routine' || kind === 'real';
}

export const DONATION_PAYMENT_PRECONDITIONS = ['DONATION_CANCEL_REQUIRES_REVERSAL'] as const;
export type DonationPaymentPrecondition = (typeof DONATION_PAYMENT_PRECONDITIONS)[number];

/**
 * The one place the operational axis consults the financial one, and it is a
 * refusal rather than a rewrite: cancelling a donation whose money has moved
 * needs a reversal in the same transaction, which needs
 * `reconciliation.reverse` — the caller decides whether it holds it. 0077
 * took the other, `rejected`'s, with the status.
 *
 * Returns `null` when the payment axis raises nothing against the move.
 */
export function paymentPreconditionFor(
  to: DonationStatus,
  paymentStatus: PaymentStatus,
): DonationPaymentPrecondition | null {
  if (to === 'cancelled' && !hasReceivedNoMoney(paymentStatus)) {
    return 'DONATION_CANCEL_REQUIRES_REVERSAL';
  }
  return null;
}

export type CancelCompensation = 'compensate' | 'none';

/**
 * What cancelling a donation owes the ledger. `partially_settled` and `settled`
 * hold money that has demonstrably arrived, so the cancellation must write a
 * compensating entry in its own transaction and the caller must say whether it
 * is a refund or a reversal. `refunded` and `reversed` have already been
 * compensated, and `unpaid` never received anything, so both need nothing but
 * the `reconciliation.reverse` permission `paymentPreconditionFor` asks for —
 * docs/decisions/0044-a-match-is-an-allocation-a-person-confirms.md §6.
 */
export function cancelCompensationFor(paymentStatus: PaymentStatus): CancelCompensation {
  return paymentStatus === 'partially_settled' || paymentStatus === 'settled'
    ? 'compensate'
    : 'none';
}

/**
 * What happens to money a cancelled donation has received, chosen at the moment
 * of cancelling because the machine cannot reverse first and refund later —
 * `reversed → refunded` is not an edge. `refund` keeps the line allocated, now
 * owed back; `reverse` frees it and returns it to the unmatched queue.
 * Re-attribution to another campaign is deliberately not an outcome —
 * docs/decisions/0044-a-match-is-an-allocation-a-person-confirms.md §6.
 */
export const CANCEL_PAYMENT_OUTCOMES = ['refund', 'reverse'] as const;
export type CancelPaymentOutcome = (typeof CANCEL_PAYMENT_OUTCOMES)[number];
export const cancelPaymentOutcomeSchema = z.enum(CANCEL_PAYMENT_OUTCOMES);

export const DONATION_LIFECYCLE_ACTIONS = ['queue', 'dequeue', 'fulfill', 'cancel'] as const;
export type DonationLifecycleAction = (typeof DONATION_LIFECYCLE_ACTIONS)[number];

export type DonationLifecycleEdge = {
  readonly action: DonationLifecycleAction;
  readonly from: DonationStatus;
  readonly to: DonationStatus;
  readonly permission: Permission;
  /**
   * Which institution the scope test reads. Cancellation tests the donation's
   * own `institution_id`; queueing and fulfillment test
   * `fulfillment_institution_id` falling back to it, because the move is the
   * yurt's work (docs/decisions/0076).
   */
  readonly scopeOn: 'entry' | 'fulfillment';
  readonly requiresReason: boolean;
};

/**
 * 0023's transition table as 0077 left it, as data: every row of it, with the
 * endpoint that moves it, the permission it needs and which institution its
 * scope test reads. `donation.transitions` filters this rather than a screen
 * holding a copy of the machine that can drift.
 */
export const DONATION_LIFECYCLE_EDGES: readonly DonationLifecycleEdge[] = [
  {
    action: 'queue',
    from: 'pending',
    to: 'queued',
    permission: 'donation.queue',
    scopeOn: 'fulfillment',
    requiresReason: false,
  },
  {
    action: 'dequeue',
    from: 'queued',
    to: 'pending',
    permission: 'donation.queue',
    scopeOn: 'fulfillment',
    requiresReason: false,
  },
  {
    action: 'fulfill',
    from: 'pending',
    to: 'fulfilled',
    permission: 'donation.fulfill',
    scopeOn: 'fulfillment',
    requiresReason: false,
  },
  {
    action: 'fulfill',
    from: 'queued',
    to: 'fulfilled',
    permission: 'donation.fulfill',
    scopeOn: 'fulfillment',
    requiresReason: false,
  },
  ...DONATION_STATUSES.filter((from) => from !== 'cancelled').map((from) => ({
    action: 'cancel' as const,
    from,
    to: 'cancelled' as const,
    permission: 'donation.cancel' as const,
    scopeOn: 'entry' as const,
    requiresReason: true,
  })),
];

export function donationLifecycleEdgesFrom(from: DonationStatus): readonly DonationLifecycleEdge[] {
  return DONATION_LIFECYCLE_EDGES.filter((edge) => edge.from === from);
}

export function donationLifecycleEdge(
  from: DonationStatus,
  action: DonationLifecycleAction,
): DonationLifecycleEdge | null {
  return (
    DONATION_LIFECYCLE_EDGES.find((edge) => edge.from === from && edge.action === action) ?? null
  );
}
